import { useMutation } from "@tanstack/react-query";
import { type AxiosError, automaticImportApi } from "libs/api";
import type { ExtendedScrapingErrorResponse } from "libs/codegen/property-onboarding";
import { useState } from "react";
import { useNavigate, useParams } from "router";
import { scrapingErrorResponseMapper } from "./scrapingErrorResponseMapper.ts";
import { useSetURLInput } from "./urlInputAtom";

export const useURLValidationHandler = () => {
    const [input, setInput] = useState<string>("");

    const [error, setError] = useState<ExtendedScrapingErrorResponse["errorType"] | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const setUrlInput = useSetURLInput();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic");
    const navigate = useNavigate();

    const { mutate, isLoading } = useMutation({
        mutationFn: (url: string) => {
            setError(null);
            setErrorMessage("");

            if (url.trim().length === 0)
                throw {
                    data: {
                        errorType: scrapingErrorResponseMapper.EmptyInput,
                    },
                } as AxiosError<Pick<ExtendedScrapingErrorResponse, "errorType">>;

            return automaticImportApi.validateUrl(lang, url);
        },
        onSuccess: ({ data }) => {
            setUrlInput(data);
            navigate("/:lang/list-my-property/get-started/automatic/lists", {
                params: {
                    lang,
                },
            });
        },
        onError: (error: AxiosError<ExtendedScrapingErrorResponse>) => {
            setError(error.data.errorType ?? scrapingErrorResponseMapper.UnexpectedError);
            setErrorMessage(error.data.message ?? "");
        },
    });

    const onBack = () => {
        navigate("/:lang/list-my-property/get-started", {
            params: { lang },
        });
    };

    const onNext = () => {
        mutate(input);
    };

    const handleSetInput = (value: string) => {
        if (error !== null) {
            setError(null);
            setErrorMessage("");
        }
        setInput(value);
    };

    return {
        input,
        setInput: handleSetInput,
        isLoading,
        error,
        errorMessage,
        onBack,
        onNext,
    };
};
